import React, { useState, useEffect } from 'react';
import { 
  isWebAuthnSupported, 
  isPlatformAuthenticatorAvailable, 
  registerPasskey, 
  authenticateWithPasskey
} from '../utils/passkeys';
import './LoginPage.css';

interface LoginPageProps {
  onLoginSuccess: (sessionToken: string) => void;
}

// Cookie helper functions
const setCookie = (name: string, value: string, days: number = 365) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Strict`;
};

const getCookie = (name: string): string | null => {
  const nameEQ = name + '=';
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

const LoginPage: React.FC<LoginPageProps> = ({ onLoginSuccess }) => {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [username, setUsername] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [supported, setSupported] = useState(false);
  const [platformAuthAvailable, setPlatformAuthAvailable] = useState(false);

  useEffect(() => {
    // Check WebAuthn support
    setSupported(isWebAuthnSupported());
    
    // Check platform authenticator availability
    isPlatformAuthenticatorAvailable().then(setPlatformAuthAvailable);
    
    // Load last successful username from cookie
    const savedUsername = getCookie('lastUsername');
    if (savedUsername) {
      setUsername(savedUsername);
    }
  }, []);

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      await registerPasskey(username);
      setSuccess('Passkey registered successfully! You can now log in.');
      setMode('login');
      setUsername('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      const sessionToken = await authenticateWithPasskey(username);
      // Save username to cookie on successful login
      setCookie('lastUsername', username);
      onLoginSuccess(sessionToken);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  if (!supported) {
    return (
      <div className="login-container">
        <div className="login-card">
          <h2>WebAuthn Not Supported</h2>
          <p className="error-message">
            Your browser doesn't support WebAuthn/Passkeys. Please use a modern browser like Chrome, Edge, Safari, or Firefox.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <img 
            src="/dashboard/aff_logo.svg" 
            alt="Affinidi Logo" 
            style={{ maxWidth: '200px', marginBottom: '0px' }}
          />
          <h1>Affinidi Trust Fabric<br/>Trust Gateway</h1>
          <h2>{mode === 'login' ? 'Sign In' : 'Register'}</h2>
        </div>

        {error && (
          <div className="alert alert-error">
            {error}
          </div>
        )}

        {success && (
          <div className="alert alert-success">
            {success}
          </div>
        )}

        <form onSubmit={mode === 'login' ? handleLogin : handleRegister}>
          <div className="form-group">
            <label htmlFor="username">Username</label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter your username"
              required
              disabled={loading}
              autoComplete="username"
              autoFocus
            />
          </div>

          <button
            type="submit"
            className="btn-primary"
            disabled={loading || !username.trim()}
          >
            {loading ? (
              <>
                <span className="spinner"></span>
                {mode === 'login' ? 'Authenticating...' : 'Registering...'}
              </>
            ) : (
              <>
                <i className={`fas ${mode === 'login' ? 'fa-fingerprint' : 'fa-user-plus'}`}></i>
                {mode === 'login' ? 'Sign in with Passkey' : 'Register Passkey'}
              </>
            )}
          </button>
        </form>

        <div className="login-footer">
          {mode === 'login' ? (
            <p>
              Don't have a passkey?{' '}
              <button
                className="link-button"
                onClick={() => {
                  setMode('register');
                  setError(null);
                  setSuccess(null);
                }}
                disabled={loading}
              >
                Register here
              </button>
            </p>
          ) : (
            <p>
              Already have a passkey?{' '}
              <button
                className="link-button"
                onClick={() => {
                  setMode('login');
                  setError(null);
                  setSuccess(null);
                }}
                disabled={loading}
              >
                Sign in here
              </button>
            </p>
          )}
        </div>

        {platformAuthAvailable && (
          <div className="platform-auth-note">
            <i className="fas fa-shield-alt"></i>
            Biometric authentication available on this device
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginPage;
